/**
 * Pagination — thanh phân trang dùng chung cho mọi danh sách.
 *
 * Chủ dự án: *"thống nhất tất cả những danh sách sẽ có 1 ô chọn số lượng hiển
 * thị trên 1 trang sau đó là nút trước sau như bên audit log"*.
 *
 * Trước đó bốn màn có bốn kiểu khác nhau:
 *
 *   | Màn            | ô chọn số dòng | Trước/Sau | "Trang x/y" |
 *   |----------------|----------------|-----------|-------------|
 *   | Tên miền       | có             | có        | có          |
 *   | Nhật ký        | —              | có        | có          |
 *   | Người dùng     | —              | có        | có          |
 *   | DNS records    | —              | có        | —           |
 *
 * Bốn bản sao là bốn chỗ phải sửa mỗi lần đổi, và `OQ-261` vừa cho thấy hậu quả:
 * hai trong bốn dùng nhầm khoá dịch, mà chỉ một nơi phô ra.
 *
 * ── Vì sao `perPage` là tham số chứ không cố định ─────────────────────────
 *
 * `FR-038` từng đòi DNS records **50 dòng** một trang trong khi `docs/04` đặt
 * mặc định chung là **25**. Chủ dự án bỏ ngoại lệ ấy ở OQ-263 — nay mọi màn đều
 * 25. Tham số vẫn giữ, vì component không nên là nơi quyết định con số ấy.
 */

import { useT } from '../lib/i18n';
import { PER_PAGE_OPTIONS } from '../lib/phan-trang';

export { PER_PAGE_OPTIONS };

export function Pagination({
  page,
  perPage,
  total,
  onPage,
  onPerPage,
  odId,
}: {
  page: number;
  perPage: number;
  total: number;
  onPage: (next: number) => void;
  onPerPage: (next: number) => void;
  odId?: string;
}) {
  const t = useT();
  const totalPages = Math.max(1, Math.ceil(total / perPage));

  return (
    <>
      <select
        className="lvl loc-gon"
        aria-label={t('common.perPage')}
        title={t('common.perPage')}
        value={String(perPage)}
        onChange={(event) => onPerPage(Number(event.target.value))}
        {...(odId ? { 'data-od-id': `${odId}-per-page` } : {})}
      >
        {PER_PAGE_OPTIONS.map((n) => (
          <option key={n} value={n}>
            {n}
          </option>
        ))}
      </select>
      <button
        className="btn btn-sm"
        type="button"
        disabled={page <= 1}
        onClick={() => onPage(Math.max(1, page - 1))}
        {...(odId ? { 'data-od-id': `${odId}-prev` } : {})}
      >
        {t('common.prev')}
      </button>
      {/*
        Chỉ báo trang KHÔNG ẩn khi chỉ có một trang.

        Nút mờ mà không nói gì thì trông như nút hỏng — đó đúng là điều chủ dự án
        gặp ở OQ-261: bấm không có tác dụng, và không có gì giải thích rằng họ
        đang ở trang duy nhất.
      */}
      <span className="small muted num">{t('common.pageOf', { page, total: totalPages })}</span>
      <button
        className="btn btn-sm"
        type="button"
        disabled={page >= totalPages}
        onClick={() => onPage(page + 1)}
        {...(odId ? { 'data-od-id': `${odId}-next` } : {})}
      >
        {t('common.next')}
      </button>
    </>
  );
}
